import NumberFormatter from "../../util/NumberFormatter";
import BigNum from "../math/BigNum";
import MonsterDescription from "../monster/MonsterDescription";
import TileType from "./TileType";
import { TileTypeRarity } from "./TileTypeRarity";
import { getRuby, getGold, getOre, getUpgrade } from "./TileTypes";

const workingRowDepth = new BigNum(0);

export default class LayerDescription {
    /**
     * @param {TileType} primaryTileType 
     * @param {number} nominalIndex The layer index presented to the player. Air and grass should be -1.
     */
    constructor(primaryTileType, nominalIndex) {

        /**
         * @constant
         * @type {TileType}
         */
        this.primaryTileType = primaryTileType;
        /**
         * @constant
         * @type {string}
         */
        this.id = primaryTileType.id;
        /**
         * @constant
         * @type {string}
         */
        this.name = primaryTileType.name;
        /**
         * @constant
         * @type {number}
         */
        this.nominalIndex = nominalIndex;
        /**
         * @constant
         * @type {string}
         */
        this.iconFileName = primaryTileType.iconFileName;

        /**
         * See initializeDepths()
         * @type {number}
         */
        this.minDepth = 0;
        /**
         * See initializeDepths()
         * @type {number}
         */
        this.maxDepth = 0;


        /**
         * @type {number}
         */
        this.oreStep = new BigNum(0);        
        /**
         * @type {number}
         */
        this.baseOre = new BigNum(0);        

        /**
         * @constant
         * @type {BigNum}
         */
        this.healthStep = new BigNum(0);

        /**
         * @constant
         * @type {BigNum}
         */
        this.baseHealth = new BigNum(0);

        /**
         * @type {BigNum}
         */
        this.baseGold = null;
        /**
         * @type {number}
         */
        this.additiveBonusFactor = 0;  // <-- not used anymore
        /**
         * @type {number}
         */
        this.multiplicativeBonusFactor = 0;

        /**
         * @constant
         * @type {BigNum}
         */
        this.maxHealth = new BigNum(0);

        /**
         * @constant
         * @type {TileType}
         */
        this.oreTileType = getOre(primaryTileType);
        /**
         * @constant
         * @type {TileType}
         */
        this.goldTileType = getGold(primaryTileType);
        /**
         * @constant
         * @type {TileType}
         */
        this.upgradeTileType = getUpgrade(primaryTileType);
        /**
         * @constant
         * @type {TileType}
         */
        this.rubyTileType = getRuby(primaryTileType);

        /**
         * @type {LayerDescription}
         */
        this.prevLayer = null;
        /**
         * @type {LayerDescription}
         */
        this.nextLayer = null;

        /**
         * @type {Array.<MonsterDescription>}
         */
        this.monsterDescriptions = [];

        // Set the backpointers to the tile layer descriptions

        if (primaryTileType.layerDescription) {
            console.log('LayerDescription ERROR: primary tile type already has a layer desc. Tile Reuse? layer: ' + this.id + ' tileType=' + primaryTileType.id);
        }
        
        primaryTileType.layerDescription = this;
        
        if (this.oreTileType) {
            if (this.oreTileType.layerDescription) {
                console.log('LayerDescription ERROR: ore tile type already has a layer desc. Tile Reuse? layer: ' + this.id + ' tileType=' + this.oreTileType.id);
            }

            if (this.oreTileType.rarityModifier !== TileTypeRarity.ORE) {
                console.log('LayerDescription ERROR layer=' + this.name + ': ORE tile type with rarity: ' + this.oreTileType.rarityModifier);
            }

            this.oreTileType.layerDescription = this;
        }
        if (this.goldTileType) {
            if (this.goldTileType.layerDescription) {
                console.log('LayerDescription ERROR: GOLD tile type already has a layer desc. Tile Reuse? layer: ' + this.id + ' tileType=' + this.goldTileType.id);
            }

            if (this.goldTileType.rarityModifier !== TileTypeRarity.GOLD) {
                console.log('LayerDescription ERROR layer=' + this.name + ': GOLD tile type with rarity: ' + this.goldTileType.rarityModifier);
            }

            this.goldTileType.layerDescription = this;
        }
        if (this.upgradeTileType) {
            if (this.upgradeTileType.layerDescription) {
                console.log('LayerDescription ERROR: upgrade tile type already has a layer desc. Tile Reuse? ' + this.id + ' tileType=' + this.upgradeTileType.id);
            }

            if (this.upgradeTileType.rarityModifier !== TileTypeRarity.UPGRADE) {
                console.log('LayerDescription ERROR layer=' + this.name + ': UPGRADE tile type with rarity: ' + this.upgradeTileType.rarityModifier);
            }

            this.upgradeTileType.layerDescription = this;
        }
        if (this.rubyTileType) {
            if (this.rubyTileType.layerDescription) {
                console.log('LayerDescription ERROR: RUBY tile type already has a layer desc. Tile Reuse? ' + this.id + ' tileType=' + this.rubyTileType.id);
            }

            if (this.rubyTileType.rarityModifier !== TileTypeRarity.RUBY) {
                console.log('LayerDescription ERROR layer=' + this.name + ': RUBY tile type with rarity: ' + this.rubyTileType.rarityModifier);
            }

            this.rubyTileType.layerDescription = this;
        }
    }

    /**
     * Returns true if this layer is deeper than the specified layer.
     * @param {LayerDescription} layerDescription 
     * @returns {boolean}
     */
    isDeeperThan(layerDescription) {
        return this.minDepth > layerDescription.minDepth;
    }

    /**
     * @param {number} depth 
     * @returns {number}
     */
    getDepthPercent(depth) {
        if (depth <= this.minDepth) {
            return 0;
        }
        if (depth >= this.maxDepth) {
            return 100;
        }

        const num = depth - this.minDepth;
        const denom = this.maxDepth - this.minDepth;

        return (num / denom) * 100;
    }

    /**
     * @param {number} baseGold
     * @param {number} additiveBonusFactor
     * @param {number} multiplicativeBonusFactor
     * @param {BigNum} oreStep
     * @param {BigNum} healthStep How much the tile health increases per tile row
     */
    initializeLayerValues(baseGold, additiveBonusFactor, multiplicativeBonusFactor, oreStep, healthStep) {
        this.oreStep.copy(oreStep);
        this.healthStep.copy(healthStep);

        this.baseGold = new BigNum(baseGold); // Gold is always just 1, so this is fine
        this.additiveBonusFactor = additiveBonusFactor;
        this.multiplicativeBonusFactor = multiplicativeBonusFactor;
    }

    /**
     * Invoked once to let us figure out the min/max depths.
     * @param {LayerDescription} prevLayer 
     * @param {number} layerThickness -1 means no limit to thickness
     * @param {boolean} firstRealLayer  True if this is the dirt layer
     */
    initializeLayer(prevLayer, layerThickness, firstRealLayer) {
        this.prevLayer = prevLayer;

        // Set the prev layer's next layer.
        if (this.prevLayer) {
            this.prevLayer.nextLayer = this;
        }

        this.minDepth = prevLayer.maxDepth;

        if (layerThickness === -1) {
            this.maxDepth = this.minDepth + 1E9;
        }
        else {
            this.maxDepth = this.minDepth + layerThickness;
        }

        if (firstRealLayer) {
            // Don't reference the GRASS layer to get previous layer values.
            this.baseHealth.copy(this.healthStep);
            this.baseOre.copy(this.oreStep);
        }
        else if (prevLayer) {
            // Use the previous layer to calculate our base health.
            const layers = prevLayer.maxDepth - prevLayer.minDepth;

            this.baseHealth.setValue(layers);
            this.baseHealth.mul(prevLayer.healthStep);
            this.baseHealth.add(prevLayer.baseHealth);
            this.baseHealth.add(this.healthStep); // add 1 row of health step for this layer

            //console.log('LAYER ' + this.name + 'prev layers=' + layers + ' prevStep=' + prevLayer.healthStep + ' prevBase=' + prevLayer.baseHealth + ' BASEHEALTH=' + NumberFormatter.formatBigNum(this.baseHealth));

            this.baseOre.setValue(layers);
            this.baseOre.mul(prevLayer.oreStep);
            this.baseOre.add(prevLayer.baseOre);
            this.baseOre.add(this.oreStep); // add 1 row of ore step for the 1st row of this layer.

            //console.log('LAYER INIT: ' + this.name + ' layers=' + layers + ' oreStep=' + NumberFormatter.formatBigNum(this.oreStep) + ' baseOre=' + NumberFormatter.formatBigNum(this.baseOre));
        }
        else {
            // We should never end up here.
            console.log('LayerDescription.initializeLayer() REACHED UNEXPECTED BLOCK. name=' + this.name);
            this.baseHealth.setValue(1);    
            this.baseOre.setValue(1);
        }

        // Calculate the health of the max row of the tile
        this.calculateTileHealth(this.maxDepth, this.maxHealth);

        //////////////////////////////////////////////////////////////

        console.log('Layer: ' + this.name + ' minDepth=' + this.minDepth + ' maxDepth=' + this.maxDepth 
            + ' baseHealth=' + NumberFormatter.formatBigNum(this.baseHealth) 
            + ' maxHealth=' + NumberFormatter.formatBigNum(this.maxHealth)
            + ' baseOre=' + NumberFormatter.formatBigNum(this.baseOre));
    }   

    /**
     * @param {number} rowDepth The row depth (the tile depth, not the pixel depth)
     * @param {BigNum} oreOutput
     */
    calculateTileOre(rowDepth, oreOutput) {
        // Old way:
        // const tileRow = Math.max(1, rowDepth);
        // oreOutput.setValue(this.tileType.layerDescription.baseOre * tileRow);

        const rows = Math.max(1, rowDepth) - this.minDepth;
        if (rows <= 0) {
            oreOutput.copy(this.baseOre);
        }
        else {
            oreOutput.copy(this.oreStep);
            oreOutput.mulNumber(rows);
            oreOutput.add(this.baseOre);

            // console.log('LayerDescription.calculateTileOre() layer=' + this.name + ' rows=' + rows 
            //     + ' step=' + NumberFormatter.formatBigNum(this.oreStep) 
            //     + ' base=' + NumberFormatter.formatBigNum(this.baseOre) 
            //     + ' result=' + NumberFormatter.formatBigNum(oreOutput))
        }
    }

        ///////////////////////////////////////////////////////////
        // const tempHealth = new BigNum(0);
        // this.tileType.layerDescription.calculateTileHealth(this.getTileRow(), tempHealth);
        // console.log('row=' + tileRow + ' ore=' + NumberFormatter.formatBigNum(oreOutput) + ' health=' + NumberFormatter.formatBigNum(tempHealth));

    /**
     * @param {number} rowDepth The row depth (the tile depth, not the pixel depth)
     * @param {BigNum} healthOutput The output param.
     */
    calculateTileHealth(rowDepth, healthOutput) {
        const rows = Math.max(1, rowDepth) - this.minDepth;
        if (rows <= 0) {
            healthOutput.copy(this.baseHealth);
        }
        else {
            healthOutput.copy(this.healthStep);
            healthOutput.mulNumber(rows);
    
            healthOutput.add(this.baseHealth);
        }
    }

    /**
     * @param {BigNum} health 
     * @returns {number}
     */
    calculateTileDepth(health) {
        // Check health against min/max health
        if (health.lessThanEquals(this.baseHealth)) {
            return this.minDepth;
        }
        else if (health.greaterThanEquals(this.maxHealth)) {
            return this.maxDepth;
        }

        // Calc number of rows past minRow
        workingRowDepth.copy(health);
        workingRowDepth.sub(this.baseHealth);
        workingRowDepth.div(this.healthStep);

        workingRowDepth.addNumber(this.minDepth);

        return Math.max(0, workingRowDepth.toNumber() | 0);
    }

    /**
     * 
     * @param {number} rowNumber 
     * @returns {boolean}
     */
    containsRow(rowNumber) {
        return this.maxDepth <= rowNumber && this.maxDepth > rowNumber;
    }
}   